'use client';

import { Dialog as BaseOverlay, useDialogStore } from '@ariakit/react';
import downloadOnTheAppStoreBadgePng from '@ui/Dallas/Images/DownloadOnTheAppStoreBadge/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg';
import getItOnGooglePlayBadgePng from '@ui/Dallas/Images/GetItOnGooglePlayBadge/google-play-badge.png';
import Image from 'next/image';
import { useEffect } from 'react';
import useTrackAnalyticsEventTruckSmarterAppUpsell from '../../_lib/analytics/useTrackAnalyticsEventTruckSmarterAppUpsell';
import DallasSvgIconCloseOutlined from '../Dallas/Icons/DallasSvgIconCloseOutlined';
import DallasImageAndroidMockUpLoadBoard from '../Dallas/Images/AndroidMockUpLoadBoard/DallasImageAndroidMockUpLoadBoard';
import TruckSmarterAppIcon from '../Dallas/Images/TruckSmarterAppIcon/TruckSmarterAppIcon';
import DallasImageiPhoneMockUpLoadBoard from '../Dallas/Images/iPhoneMockUpLoadBoard/DallasImageiPhoneMockUpLoadBoard';
import DallasLink from '../Dallas/Link/DallasLink';
import DallasStack from '../Dallas/Stack/DallasStack';
import DallasText from '../Dallas/Text/DallasText';
import TruckSmarterAppUpsellBanner from './TruckSmarterAppUpsellBanner';
import TruckSmarterAppUpsellPlatform from './TruckSmarterAppUpsellPlatform';
import BRANCH_LINK from './branch-link';

import styles from './TruckSmarterAppUpsellDialog.module.scss';

interface Props {
  platform: TruckSmarterAppUpsellPlatform;
  onDismiss: () => void;
}

export default function TruckSmarterAppUpsellDialog({
  platform,
  onDismiss,
}: Props) {
  const track = useTrackAnalyticsEventTruckSmarterAppUpsell();

  function dismiss() {
    track({
      type: 'APP_UPSELL_DIALOG_DISMISSED',
      metadata: {},
    });
    onDismiss();
  }

  const dialogStore = useDialogStore({
    open: true,
    setOpen: (open) => !open && dismiss(),
  });

  useEffect(() => {
    track({
      type: 'APP_UPSELL_DIALOG_RENDERED',
      metadata: {},
    });
  }, [track]);

  return (
    <BaseOverlay
      className={styles.root}
      store={dialogStore}
      modal={true}
      portal={true}
    >
      <TruckSmarterAppUpsellBanner />
      <DallasStack
        className={styles.dialogContent}
        gap="2xl"
        alignHorizontal="center"
        flex
      >
        <DallasStack
          gap="md"
          alignHorizontal="center"
          paddingTop="md"
          paddingHorizontal="md"
        >
          <DallasStack direction="row" padding="none" alignHorizontal="end">
            <button className={styles.closeButton} onClick={dismiss}>
              <DallasSvgIconCloseOutlined />
            </button>
          </DallasStack>
          <TruckSmarterAppIcon width={72} />
          <DallasText
            variant="headerCondensedS"
            align="center"
            color="light"
            as="h2"
          >
            YOU WANT THE APP,
            <br />
            TRUST US
          </DallasText>
          <DallasText color="light" align="center">
            It&apos;s way better on your phone! Easier search, faster bid/book,
            and loads more features, like fuel discounts and load alerts.
          </DallasText>
        </DallasStack>
        <DallasLink
          href={BRANCH_LINK}
          onClick={() =>
            track({
              type: 'APP_UPSELL_DIALOG_CTA_PRESSED',
              metadata: {},
            })
          }
        >
          {platform === TruckSmarterAppUpsellPlatform.IOS ? (
            <Image
              src={downloadOnTheAppStoreBadgePng}
              alt="Download on the App Store"
              width={150}
            />
          ) : (
            <Image
              src={getItOnGooglePlayBadgePng}
              alt="Get it on Google Play"
              width={150}
            />
          )}
        </DallasLink>
        {platform === TruckSmarterAppUpsellPlatform.IOS ? (
          <DallasImageiPhoneMockUpLoadBoard
            className={styles.mobileMockUpLoadBoard}
          />
        ) : (
          <DallasImageAndroidMockUpLoadBoard
            className={styles.mobileMockUpLoadBoard}
          />
        )}
      </DallasStack>
    </BaseOverlay>
  );
}
